import { useCallback, useState } from 'react';
import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';

const read = (key: string): GridColumnVisibilityModel | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Column visibility kept in localStorage. Every column gets an explicit key: a missing key would
 * fall back to visible in DataGridPro, so a newly added column takes its default visibility.
 */
export const useStoredColumns = (key: string, defaultModel: GridColumnVisibilityModel) => {
  const [stored, setStored] = useState<GridColumnVisibilityModel | null>(() => read(key));
  const [storedKey, setStoredKey] = useState(key);
  // Another key (template switch): reload what was saved for it.
  if (storedKey !== key) {
    setStoredKey(key);
    setStored(read(key));
  }

  const model: GridColumnVisibilityModel = { ...defaultModel };
  Object.keys(defaultModel).forEach((field) => {
    if (stored && typeof stored[field] === 'boolean') model[field] = stored[field];
  });

  const setModel = useCallback(
    (next: GridColumnVisibilityModel) => {
      const explicit: GridColumnVisibilityModel = {};
      Object.keys(defaultModel).forEach((field) => {
        explicit[field] = next[field] !== false;
      });
      setStored(explicit);
      localStorage.setItem(key, JSON.stringify(explicit));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, JSON.stringify(defaultModel)]
  );

  return [model, setModel] as const;
};
